<template>
  <div class="page-content">
    <h1>{{ $t('v.blackList.title') }}</h1>

    <v-section>
      <v-toolbar :title="$t('v.blackList.titlePlacements')">
        <template #default>
          <v-select
            :value="level"
            :options="levelsOptions"
            :label="'name'"
            :placeholder="$t('c.select.placeholderSelectCampaign')"
            :disabled="loading || campaignLoading"
            :allow-empty="true"
            @input="changeLevel(blacklistType, $event)"
          />
        </template>
        <template #secondRowToolsLeft>
          <v-button
            theme="danger"
            :disabled="!canChangeBlacklistPlacements"
            @click="$refs.deleteDialog.open()"
          >{{ $t('v.blackList.buttonDelete') }}</v-button>

          <v-button
            theme="info"
            :disabled="!canChangeBlacklistPlacements"
            @click="$refs.whiteListDialog.open()"
          >{{ $t('v.blackList.buttonMoveToWhiteList') }}</v-button>
        </template>

        <template #secondRowToolsRight>
          <v-button
            theme="primary"
            :disabled="!canChangeBlacklistPlacements || !isCampaignLevel"
            @click="$refs.manuallyDialog.open()"
          >{{ $t('v.blackList.buttonAddManually') }}</v-button>

          <v-button
            theme="primary"
            :disabled="!canChangeBlacklistPlacements || !isCampaignLevel"
            @click="$refs.fileDialog.open()"
          >{{ $t('v.blackList.buttonAddFromFile') }}</v-button>
        </template>
      </v-toolbar>

      <hr />

      <div class="table-wrapper">
        <v-placements-table-black-list
          v-loading="loading"
          ref="blackListTable"
          :data="placements"
          :cpl="accountCPL"
          :default-sort="sort"
          @sort-change="changeSort(blacklistType, $event)"
          @selection-change="selection = $event"
        />

        <v-row justify="center" class="pagination-wrapper" v-if="totalPage">
          <v-pagination
            layout="prev, pager, next"
            :page-size="1"
            :current-page="currentPage"
            :total="totalPage"
            :disabled="loading"
            @current-change="changeCurrentPage(blacklistType, $event)"
          />
        </v-row>
      </div>

      <a
        class="blacklist-download-list"
        v-if="canChangeBlacklistPlacements && isCampaignLevel"
        @click="downloadCampaignBlacklist">{{ $t('v.blackList.buttonDownloadList') }}</a>
    </v-section>

    <!-- dialogs -->
    <!-- delete from list -->
    <v-confirmation-dialog
      v-if="canChangeBlacklistPlacements"
      ref="deleteDialog"
      :title="$t('c.dialog.titleDeleteFromBlackList')"
      :message="$t('c.dialog.messageDeleteFromBlackList')"
      @confirm="deletePlacementsFromList()"
    />

    <v-first-ban-confirmation-dialog ref="manuallyDialog" v-if="canChangeBlacklistPlacements">
      <template #default="{ step, resetStep }">
        <v-placements-dialog-input
          :visible="step === 2"
          :type="blacklistType"
          :action="addBlacklistManuallyType"
          @change="fetchPlacements(blacklistType)"
          @close="resetStep()"
        />
      </template>
    </v-first-ban-confirmation-dialog>

    <v-first-ban-confirmation-dialog ref="fileDialog" v-if="canChangeBlacklistPlacements">
      <template #default="{ step, resetStep }">
        <v-placements-dialog-file-upload
          :visible="step === 2"
          :type="blacklistType"
          :action="addBlacklistManuallyType"
          @uploaded="fetchPlacements(blacklistType)"
          @close="resetStep()"
        />
      </template>
    </v-first-ban-confirmation-dialog>

    <v-placements-dialog-switch
      v-if="!canChangeBlacklistPlacements && canChangeWhitelistPlacements"
      ref="whiteListDialog"
      :selection="selection"
      @moved="fetchPlacements(blacklistType)"
    />
  </div>
</template>

<script>
import downloadFile from 'js-file-download'
import VSelect from '@/components/common/VSelect.vue'
import VPagination from '@/components/common/VPagination.vue'
import VConfirmationDialog from '@/components/common/VConfirmationDialog.vue'
import VFirstBanConfirmationDialog from '@/components/common/VFirstBanConfirmationDialog.vue'
import VPlacementsDialogSwitch from '@/components/Placements/VPlacementsDialogSwitch.vue'
import VPlacementsDialogInput from '@/components/Placements/VPlacementsDialogInput.vue'
import VPlacementsDialogFileUpload from '@/components/Placements/VPlacementsDialogFileUpload.vue'
import VPlacementsTableBlackList from '@/components/Placements/VPlacementsTableBlackList.vue'
import VToolbar from '@/components/common/VToolbar.vue'
import VSection from '@/components/common/VSection.vue'
import VRow from '@/components/common/VRow.vue'
import * as types from '@/store/types'
import * as authorities from '@/utils/authorities'
import storeUtils from '@/mixins/storeUtils'

export default {
  name: 'BlackList',

  components: {
    VSelect,
    VPagination,
    VConfirmationDialog,
    VFirstBanConfirmationDialog,
    VPlacementsDialogSwitch,
    VPlacementsDialogInput,
    VPlacementsDialogFileUpload,
    VPlacementsTableBlackList,
    VToolbar,
    VSection,
    VRow
  },

  mixins: [storeUtils],

  data() {
    return {
      blacklistType: types.BLACKLIST_PLACEMENTS,
      addBlacklistManuallyType: types.ADD_PLACEMENTS_TO_BLACKLIST_MANUALLY,
      addBlacklistFromFileType: types.ADD_PLACEMENTS_TO_BLACKLIST_FROM_FILE,
      selection: [],
      datePickerOptions: {
        firstDayOfWeek: 1
      }
      // actionToConfirm: null,
    }
  },

  computed: {
    accountCPL() {
      return this.$store.getters.accountCPL
    },
    loading() {
      return this.$store.state[this.blacklistType].loading
    },
    placements() {
      return this.$store.state[this.blacklistType].list
    },
    currentPage() {
      return this.$store.state[this.blacklistType].params.currentPage
    },
    totalPage() {
      return this.$store.state[this.blacklistType].params.totalPage
    },
    sort() {
      return this.$store.state[this.blacklistType].params.sort
    },
    isCampaignLevel() {
      return (
        this.$store.state[this.blacklistType].params.level.type === 'campaign'
      )
    },
    level() {
      return this.$store.state[this.blacklistType].params.level
    },
    canChangeBlacklistPlacements() {
      return this.$store.getters.hasAuthorities(
        authorities.CHANGE_BLACKLIST_PLACEMENTS
      )
    },
    canChangeWhitelistPlacements() {
      return this.$store.getters.hasAuthorities(
        authorities.CHANGE_WHITELIST_PLACEMENTS
      )
    },
    campaignLoading() {
      return this.$store.state[types.CAMPAIGNS].loading
    },

    levelsOptions() {
      return this.$store.getters.blacklistLevelsOptions(this.$i18n)
    }
  },

  created() {
    this.fetchCampaigns()
    const value = this.levelsOptions[0]
    this.updateValue(this.blacklistType, { key: 'level', value })
    this.fetchPlacements(this.blacklistType)
  },

  destroyed() {
    this.clearPlacements(this.blacklistType)
  },

  methods: {
    changeCurrentPage(type, value) {
      this.updateValue(type, { key: 'currentPage', value })
      this.fetchPlacements(type)
    },

    changeSort(type, { prop, order }) {
      const { prop: oldProp, order: oldOrder } = this.$store.state[
        type
      ].params.sort

      if (prop !== oldProp || order !== oldOrder) {
        this.updateValue(type, { key: 'sort', value: { prop, order } })
        this.fetchPlacements(type)
      }
    },

    changeLevel(type, value) {
      this.updateValue(type, { key: 'level', value })
      this.fetchPlacements(type)
    },

    updateValue(type, { key, value }) {
      const params = {
        [key]: value
      }
      this.updateParams(type, params)
    },

    fetchCampaigns() {
      if (!this.campaignLoading) {
        this.$store.dispatch(types.FETCH_CAMPAIGNS)
      }
    },
    downloadCampaignBlacklist() {
      const campaign = this.level.value
      this.$store.getters.apiService.exportCampaignBlacklist(campaign)
        .then((campaignBlacklist) => {
          downloadFile(campaignBlacklist, this.getCampaignBlacklistFilename())
        })
    },
    getCampaignBlacklistFilename(extension = 'xlsx') {
      const username = this.$store.state[types.SESSION].model.user.directLogin
        || this.$store.state[types.SESSION].model.user.username
      const filename = this.level.name
      return `${username} ${filename}.${extension}`
    },

    fetchPlacements() {
      if (!this.loading) this.$store.dispatch(types.FETCH_BLACKLIST_PLACEMENTS)
    },

    clearPlacements(name) {
      this.setList(name, [])
      this.updateParams(name, {
        sort: {
          prop: null,
          order: null
        },
        currentPage: 1,
        totalPage: null,
        campaign: null
      })
    },
    deletePlacementsFromList() {
      const action = 'DELETE'
      const name = this.blacklistType
      const ids = this.selection.map(item => item.id)

      if (ids.length) {
        this.$store
          .dispatch(types.CHANGE_BLACKLIST_PLACEMENTS, { name, ids, action })
          .then(() => this.fetchPlacements(name))
      }
    }

    // confirmAction(action) {
    //   if (this.isFirstBan) {
    //     this.actionToConfirm = action;
    //     this.$refs.firstBanConfirmationDialog.open();
    //   } else {
    //     action();
    //   }
    // },
    // callAction() {
    //   if (this.actionToConfirm) this.actionToConfirm();
    // },
  }
}
</script>

<style lang="scss">
.blacklist-download-list {
  margin-top: 30px;
  display: inline-block;
  &:hover {
    cursor: pointer;
  }
}
</style>
